import { useSelector } from "react-redux"
import { useEffect, useMemo } from "react"
import { CLAN_PAGES } from "pages/clans/enums/clan-pages"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { RootState } from "store/index"
import { userSelectors } from "store/apis/userAPI"
import { CLAN_ROLE } from "store/apis/clansAPI/types"
import { useGetUserClanQuery } from "store/apis/clansAPI/index"
import { dayjs } from "utils/dayjs"

export const useUserClan = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { clanId } = useParams()

  const { data: userLocation } = useSelector(
    userSelectors.selectCurrentUserLocation
  )
  const user = useSelector((state: RootState) => state.user)

  const { data: userClan, isLoading: isLoadingUserClan } = useGetUserClanQuery(
    undefined,
    {
      skip: !userLocation || userLocation < 6
    }
  )

  const userPower = useMemo(() => {
    return user.currentLuck + user.currentEndurance + user.currentDiggingSpeed
  }, [user])

  const isMyClan = useMemo(() => {
    return (userClan?.clan && !clanId) || userClan?.clanId === clanId
  }, [userClan, clanId])

  const role = useMemo(() => {
    if (!userClan) {
      return CLAN_ROLE.MEMBER
    }

    return userClan?.role
  }, [userClan, userLocation])

  const isAllowJoinToClan = useMemo(() => {
    if (!userClan) return true
    return dayjs().isAfter(dayjs(userClan.leavedClanAt).add(24, "hours"))
  }, [userClan])

  const isOwner = useMemo(() => role === CLAN_ROLE.OWNER, [role])
  const isModerator = useMemo(() => role === CLAN_ROLE.MODERATOR, [role])
  const isHelper = useMemo(() => role === CLAN_ROLE.HELPER, [role])
  const isMember = useMemo(() => role === CLAN_ROLE.MEMBER, [role])

  const isModeratorPermissions = useMemo(
    () => isOwner || isModerator,
    [isOwner, isModerator]
  )
  const isHelperPermissions = useMemo(
    () => isOwner || isModerator || isHelper,
    [isOwner, isModerator, isHelper]
  )

  useEffect(() => {
    if (isLoadingUserClan) return

    const path = location.pathname
    const isClanless = !userClan?.clan

    const clanlessRedirects = [
      "/clans/form/create",
      CLAN_PAGES.CLANS_PAGE,
      CLAN_PAGES.LEADERBOARD,
      CLAN_PAGES.NOT_FOUND
    ]
    const withClanRedirects = [CLAN_PAGES.CLANS_PAGE, "/clans/form/create"]

    if (isClanless) {
      const isAllowed =
        clanlessRedirects.includes(path) || path.startsWith("/clans/clan/")
      if (!isAllowed) {
        navigate(CLAN_PAGES.CLANS_PAGE, { replace: true })
      }
    } else {
      if (withClanRedirects.includes(path)) {
        navigate("/clans", { replace: true })
      }
    }
  }, [userClan, isLoadingUserClan, location.pathname, navigate])

  return {
    userClan,
    role,
    isOwner,
    isModerator,
    isHelper,
    isMember,
    isModeratorPermissions,
    isHelperPermissions,
    clan: userClan?.clan,
    clanId: userClan?.clanId,
    clanBalance: userClan?.clan?.balance || {
      gold: 0,
      bones: 0,
      labr: 0,
      stars: 0,
      bl: 0
    },
    isLoading: isLoadingUserClan,
    isAllowJoinToClan,
    isMyClan,
    userPower
  }
}
